import React from 'react'
import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'

import { CurrencyInput, ListItemAction, useGlobalCopyStore, useGoalStore } from '../../../common'
import { TrackType } from '../../../core'
import { formatCurrency } from '../../../core/utils/NumberFormatting'

interface ContributionActionProps {
  trackType: TrackType
}

export const ContributionAction = observer(({ trackType }: ContributionActionProps) => {
  const { goals: copy } = useGlobalCopyStore()
  const { setAlert, monthlyCashFlowProfile, updateMonthlyCashFlowProfile } = useGoalStore()

  const contribution = React.useMemo(() => {
    let amount = 0
    if (trackType === TrackType.SAVINGS_TRACK) {
      amount = monthlyCashFlowProfile?.amount_allocated_for_savings_goals || 0
    } else if (trackType === TrackType.DEBT_TRACK) {
      amount = monthlyCashFlowProfile?.amount_allocated_for_debt_goals || 0
    } else if (trackType === TrackType.RETIREMENT_TRACK) {
      amount = monthlyCashFlowProfile?.amount_allocated_for_retirement_goals || 0
    }
    return amount
  }, [monthlyCashFlowProfile])

  React.useEffect(() => {
    setAmount(contribution)
  }, [contribution])

  const [amount, setAmount] = React.useState<number>(contribution)

  const isInvalid = amount < 0 || amount > 99999999

  const handleSave = async () => {
    if (monthlyCashFlowProfile) {
      const updatedProfile = { ...monthlyCashFlowProfile }
      if (trackType === TrackType.SAVINGS_TRACK) {
        updatedProfile.amount_allocated_for_savings_goals = amount
      } else if (trackType === TrackType.DEBT_TRACK) {
        updatedProfile.amount_allocated_for_debt_goals = amount
      } else if (trackType === TrackType.RETIREMENT_TRACK) {
        updatedProfile.amount_allocated_for_retirement_goals = amount
      }

      await updateMonthlyCashFlowProfile(updatedProfile)
      setAlert(copy.alert_contribution_updated)
    }
  }

  return (
    <Stack bgcolor="background.paper">
      <ListItemAction
        isSaveDisabled={!amount || isNaN(amount)}
        label={copy.contribution_label}
        onCancel={() => setAmount(contribution)}
        onSave={handleSave}
        primaryText={copy.contribution_label}
        secondaryText={formatCurrency(contribution, '0,0.00')}
      >
        <CurrencyInput
          amount={amount}
          autoFocus={true}
          error={isInvalid}
          fullWidth={true}
          minAmount={0}
          setAmount={(value) => setAmount(!isNaN(Number(value)) ? Number(value) : amount)}
          sx={{ '.MuiTypography-body1': { p: 0 } }}
        />
      </ListItemAction>
    </Stack>
  )
})
